<script lang="ts">
const tableHeaders = [
  { text: 'Company' },
  { text: 'Side' },
  { text: 'Ticker' },
  { text: 'CUSIP' },
  { text: 'Open Qty' },
  { text: 'Fee', align: 'end' },
  { text: 'Rebate Rate', align: 'end' },
  { text: 'Exec Qty', align: 'end' },
  { text: 'Total Qty', align: 'end' },
  { text: 'Total Value', align: 'end' },
  { text: 'Time in force', align: 'end' },
];
</script>

<script setup lang="ts">
import { computed, onMounted, shallowRef } from 'vue';
import { timeInForceAbbr } from '@/modules/marketplace/helpers/marketplace';
import ItemsBatchConfirmationDialog from '@/modules/common/components/ItemsBatchConfirmationDialog.vue';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import Decimal from 'decimal.js';
import {
  FrontendOrder,
  FrontendOrderSchema,
} from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { OmsTimeInForceType } from '@/connect/gen/consts/omsconsts_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { useCompaniesStore } from '@/store/companies-store';
import { Data, create } from '@/connect';
import { marketplaceService } from '@/connect/services/marketplace';
import { useDialogManager } from '@/plugins/dialog-manager';
import { store as vuex } from '@/store/store';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

const props = defineProps<{
  items: FrontendOrder[];
}>();

type OrderWithTotalValue = Data<FrontendOrder> & {
  totalValue: Decimal;
};

const securitiesStore = useSecuritiesStore();
const companiesStore = useCompaniesStore();
const dialogManager = useDialogManager();
const snackbarManager = useSnackbarManager();

const itemsToBeCanceled = shallowRef<FrontendOrder[]>([]);

onMounted(() => {
  itemsToBeCanceled.value = [...props.items];
});

const itemsWithTotalValue = computed((): OrderWithTotalValue[] => {
  return props.items.map((item) => {
    const security = securitiesStore.getSecurity(item.cusip);
    const totalValue = security
      ? new Decimal(security.lastClosePrice ?? 0).mul(item.quantity.toString())
      : new Decimal(0);
    return Object.assign(create(FrontendOrderSchema), { ...item, totalValue });
  });
});

const description = computed((): string => {
  const orderStr = itemsToBeCanceled.value?.length > 1 ? `orders` : 'order';
  if (itemsToBeCanceled.value?.length > 0) {
    const uniqueCompanyIds = [...new Set(itemsToBeCanceled.value.map((item) => item.companyId))];
    const companyNames = uniqueCompanyIds.map((companyId) => formatCompanyName(companyId));

    let message = `You are about to force cancel ${itemsToBeCanceled.value.length} ${orderStr}`;
    if (uniqueCompanyIds.length === 1) {
      message += ` for company ${companyNames[0]}`;
    } else {
      message += ` for companies ${companyNames.join(', ')}`;
    }
    return message;
  }
  return '';
});

async function execute(): Promise<{ failedIds?: string[] }> {
  return new Promise((resolve) => {
    dialogManager.alert({
      title: 'Confirm Force Cancel',
      message: `${description.value}. This action may be detrimental.`,
      acceptText: 'Force Cancel',
      shouldManuallyConfirm: true,
      onAccept: async () => {
        try {
          const userEmail = vuex.state.loginState?.user?.emailAddress || '';
          const orderRefs = itemsToBeCanceled.value.map(({ orderRef }) => orderRef);
          const { failedOrderRefs } = await marketplaceService.brokerCancelOrders(
            orderRefs,
            userEmail
          );
          resolve({ failedIds: failedOrderRefs });
        } catch (error) {
          snackbarManager.show({ message: i18n.t('error.generic') as string });
          resolve({ failedIds: itemsToBeCanceled.value.map(({ orderRef }) => orderRef) });
        }
      },
      onReject: () => {
        // User cancelled the email confirmation, treat as if all failed
        resolve({ failedIds: itemsToBeCanceled.value.map(({ orderRef }) => orderRef) });
      },
    });
  });
}

function formatPrice(price: Decimal): string {
  return getPriceAsString(price, PRICE_PRECISION);
}

function formatTimeInForceType(item: FrontendOrder): string {
  return timeInForceAbbr(OmsTimeInForceType[item.timeInForceType]);
}

function formatCompanyName(companyId: string): string {
  const company = companiesStore.getCompany(companyId);
  return company ? `${company.companyName} (${company.displayId})` : companyId;
}
</script>

<template>
  <ItemsBatchConfirmationDialog
    :description="description"
    :execute="execute"
    item-id-key="orderRef"
    :items="itemsWithTotalValue"
    success-message="Force cancel successfully initiated."
    :table-headers="tableHeaders"
    title="Force Cancel"
    v-on="$listeners"
  >
    <template #row="{ item }">
      <td>
        {{ formatCompanyName(item.companyId) }}
      </td>
      <td>
        <FormatSide :side="item.side === Side.BORROW ? 'BORROW' : 'LOAN'" />
      </td>
      <td>
        {{ securitiesStore.getSecurity(item.cusip)?.ticker }}
      </td>
      <td>
        {{ item.cusip }}
      </td>
      <td class="text-end">
        <PrettyNumber :value="item.quantity - item.totalExecQty" />
      </td>
      <td class="text-end">
        <span v-if="item.fee === null">–</span>
        <RateOutput v-else :rate="item.fee" />
      </td>
      <td class="text-end">
        <span v-if="item.rebate === null">–</span>
        <RateOutput v-else :rate="item.rebate" />
      </td>
      <td class="text-end">
        <span v-if="!item.totalExecQty">–</span>
        <span v-else>
          <PrettyNumber :value="item.totalExecQty" />
        </span>
      </td>
      <td class="text-end">
        <PrettyNumber :value="item.quantity" />
      </td>
      <td class="text-end">${{ formatPrice(item.totalValue) }}</td>
      <td class="text-end">
        {{ formatTimeInForceType(item) }}
      </td>
    </template>
  </ItemsBatchConfirmationDialog>
</template>
